import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { ThemeEvents, DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/** Cart attribute written by the Talon.One app embed — the live source; its sibling
 *  `talon_one_discount_breakdown` never populates for automatic per-item effects on this store. */
const TALON_EFFECTS_ATTR = 'talon_one_session_effects';

/**
 * A custom element that applies a discount to the cart. Also renders an estimated discount row
 * (order-level or per-item), read from Talon.One's live session effects, until a checkout
 * round-trip confirms it.
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 * @property {HTMLElement} cartOriginalTotalContainer - Wraps the Subtotal label + discount rows.
 * @property {HTMLTemplateElement} moneyFormat - Holds `shop.money_format` for client-side formatting.
 * @property {HTMLTemplateElement} cartDiscountIcon - Discount icon markup to clone into estimated rows.
 */
/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = [
    'cartDiscountError',
    'cartDiscountErrorDiscountCode',
    'cartDiscountErrorShipping',
    'cartOriginalTotalContainer',
    'moneyFormat',
    'cartDiscountIcon',
  ];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {{ rows: { label: string, amountCents: number }[], totalDiscountCents: number, currency: string } | null} */
  #lastEstimate = null;

  /** @type {ReturnType<typeof setTimeout>[]} */
  #pollTimers = [];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.addEventListener('dialog:open', this.#onDialogOpen, true);
    void this.#refreshEstimate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener('dialog:open', this.#onDialogOpen, true);
    this.#clearPollTimers();
  }

  #clearPollTimers() {
    this.#pollTimers.forEach((timer) => clearTimeout(timer));
    this.#pollTimers = [];
  }

  /** Re-applies the cached estimate after any morph (morph.js calls this on every touched Component). */
  updatedCallback() {
    super.updatedCallback();
    if (this.#hasRealDiscountContent()) {
      this.#lastEstimate = null;
      return;
    }
    const estimate = this.#lastEstimate;
    if (estimate) this.#renderEstimateRows(estimate);
  }

  /** @param {Event} event */
  #onCartUpdate = (event) => {
    // detail.resource is the fresh cart response — use it now instead of waiting on the first poll.
    const resource = /** @type {any} */ (event).detail?.resource;
    if (resource && typeof resource === 'object') {
      void this.#refreshEstimate(resource);
    }
    this.#scheduleRefresh();
  };

  /** @param {Event} event */
  #onDialogOpen = (event) => {
    if (event.target instanceof Element && event.target.closest('cart-drawer-component')) {
      this.#scheduleRefresh();
    }
  };

  /** Backoff poll after a cart change — T1's evaluation can take several seconds, not just ~2s. */
  #scheduleRefresh() {
    this.#clearPollTimers();

    const delaysMs = [300, 1000, 2000, 4000, 7000, 10000];
    this.#pollTimers = delaysMs.map((delay) => setTimeout(() => void this.#refreshEstimate(), delay));
  }

  /** @returns {boolean} True if the container already has server-rendered (confirmed) discount content. */
  #hasRealDiscountContent() {
    const { cartOriginalTotalContainer } = this.refs;
    return Array.from(cartOriginalTotalContainer.children).some((el) => !el.hasAttribute('data-talon-estimate'));
  }

  /**
   * True once a campaign in `rows` has been baked into real `line_level_discount_allocations`
   * (e.g. after a checkout round-trip) — `cart.total_price` already reflects it once, so the
   * estimate must stop subtracting it again.
   * @param {any} cart
   * @param {{ label: string, amountCents: number }[]} rows
   * @returns {boolean}
   */
  #hasRealLineDiscount(cart, rows) {
    if (!Array.isArray(cart.items) || rows.length === 0) return false;
    const labels = new Set(rows.map((row) => row.label.toLowerCase()));
    return cart.items.some((item) =>
      item.line_level_discount_allocations?.some((alloc) =>
        labels.has(String(alloc.discount_application?.title ?? '').trim().toLowerCase())
      )
    );
  }

  /** @param {any} [cartOverride] - A cart object already in hand (e.g. a cart-update event's resource), to skip the fetch. */
  async #refreshEstimate(cartOverride) {
    /** @type {any} */
    let cart = cartOverride ?? null;
    if (!cart) {
      try {
        const res = await fetch('/cart.js');
        cart = res.ok ? await res.json() : null;
      } catch {
        cart = null;
      }
    }
    if (!cart) return;

    const effects = this.#readEffects(cart);
    const { rows, totalDiscountCents } = this.#summariseEffects(effects);
    const baseTotalCents = cartOverride ? cart.total_price : this.#inStockTotalCents(cart.total_price);

    if (this.#hasRealDiscountContent() || this.#hasRealLineDiscount(cart, rows)) {
      this.#lastEstimate = null;
      this.#clearEstimateRows();
      return;
    }

    if (totalDiscountCents <= 0) {
      this.#lastEstimate = null;
      this.#clearEstimateRows();
      this.#setAdjustedTotal(baseTotalCents, cart.currency);
      return;
    }

    // Design shows only one discount row at a time — collapse stacked campaigns into one label.
    const { cartOriginalTotalContainer } = this.refs;
    const finalRows =
      rows.length > 1
        ? [
            {
              label: cartOriginalTotalContainer.dataset.combinedDiscountLabel || 'Combined Order Discount',
              amountCents: totalDiscountCents,
            },
          ]
        : rows;

    const estimate = { rows: finalRows, totalDiscountCents, currency: cart.currency };
    this.#lastEstimate = estimate;
    this.#renderEstimateRows(estimate);
    const discountedTotalCents = baseTotalCents - totalDiscountCents;
    this.#setAdjustedTotal(discountedTotalCents, cart.currency);
    this.#syncBnplAmount(discountedTotalCents);
  }

  /**
   * The server-rendered total excludes out-of-stock lines; `cart.total_price` does not.
   * @param {number} fallbackCents - Used when the server value is unavailable.
   * @returns {number}
   */
  #inStockTotalCents(fallbackCents) {
    const raw = this.refs.cartOriginalTotalContainer.dataset.inStockTotalCents;
    const cents = raw === undefined || raw === '' ? NaN : Number(raw);
    return Number.isFinite(cents) ? cents : fallbackCents;
  }

  /**
   * Passes the discounted total to the BNPL teasers so their instalment split matches the Total line.
   * @param {number} totalCents
   */
  #syncBnplAmount(totalCents) {
    document.querySelectorAll('bnpl-messaging.bnpl-messaging-cart-summary').forEach((el) => {
      /** @type {any} */ (el).setAmount?.(Math.max(0, totalCents));
    });
  }

  /**
   * @param {any} cart
   * @returns {any[]}
   */
  #readEffects(cart) {
    try {
      const raw = cart.attributes && cart.attributes[TALON_EFFECTS_ATTR];
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * Sums automatic (non-coupon) `setDiscount`/`setDiscountPerItem` effects by campaign name.
   * Talon.One emits one `setDiscountPerItem` entry per discounted unit, so no quantity math needed.
   * @param {any[]} effects
   * @returns {{ rows: { label: string, amountCents: number }[], totalDiscountCents: number }}
   */
  #summariseEffects(effects) {
    /** @type {Map<string, number>} */
    const totalsByLabel = new Map();

    for (const e of effects) {
      if (e?.effectType !== 'setDiscount' && e?.effectType !== 'setDiscountPerItem') continue;
      if (e?.triggeredByCoupon) continue;

      const value = Number(e?.props?.value ?? 0);
      if (!(value > 0)) continue;

      // ruleName is the full campaign name; matches the confirmed post-checkout title.
      const label = String(e.ruleName ?? e.props?.name ?? 'Promotion')
        .replace(/#\d+$/, '')
        .trim();

      totalsByLabel.set(label, (totalsByLabel.get(label) ?? 0) + value);
    }

    const rows = Array.from(totalsByLabel, ([label, amount]) => ({
      label,
      amountCents: Math.round(amount * 100),
    }));

    const totalDiscountCents = rows.reduce((sum, row) => sum + row.amountCents, 0);

    return { rows, totalDiscountCents };
  }

  /**
   * Renders the Subtotal label row + one row per distinct campaign (no remove button — automatic only).
   * @param {{ rows: { label: string, amountCents: number }[], currency: string }} estimate
   */
  #renderEstimateRows(estimate) {
    const { cartOriginalTotalContainer, cartDiscountIcon } = this.refs;
    const { currency } = estimate;
    const subtotalLabel = cartOriginalTotalContainer.dataset.subtotalLabel || 'Subtotal';
    const subtotalCents = Number(cartOriginalTotalContainer.dataset.subtotalCents ?? 0);

    this.#clearEstimateRows();

    const subtotalRow = document.createElement('span');
    subtotalRow.className = 'cart__summary-item cart__original-total';
    subtotalRow.setAttribute('data-talon-estimate', '');
    subtotalRow.innerHTML =
      `<span class="cart__original-total-label">${this.#esc(subtotalLabel)}</span>` +
      `<span class="cart__original-total-value cart-secondary-typography">${this.#esc(this.#formatMoney(subtotalCents, currency))}</span>`;
    cartOriginalTotalContainer.append(subtotalRow);

    for (const row of estimate.rows) {
      const rowEl = document.createElement('span');
      rowEl.className = 'cart__summary-item cart-discount__applied';
      rowEl.setAttribute('data-talon-estimate', '');
      rowEl.innerHTML =
        '<span class="cart-discount__applied-info">' +
        '<span class="svg-wrapper cart-discount__applied-icon"></span>' +
        `<span class="cart-discount__applied-title">${this.#esc(row.label)}</span>` +
        '</span>' +
        `<span class="cart__discount-value cart-secondary-typography">-${this.#esc(this.#formatMoney(row.amountCents, currency))}</span>`;

      const iconWrapper = rowEl.querySelector('.cart-discount__applied-icon');
      if (iconWrapper) iconWrapper.append(cartDiscountIcon.content.cloneNode(true));

      cartOriginalTotalContainer.append(rowEl);
    }
  }

  #clearEstimateRows() {
    const { cartOriginalTotalContainer } = this.refs;
    cartOriginalTotalContainer.querySelectorAll('[data-talon-estimate]').forEach((el) => el.remove());
  }

  /**
   * Adjusts the Total line (outside this component's subtree) via the shared `[data-cart-subtotal]` hook.
   * @param {number} totalCents
   * @param {string} currency
   */
  #setAdjustedTotal(totalCents, currency) {
    const cartTotal = document.querySelector('[data-cart-subtotal]');
    if (!(cartTotal instanceof HTMLElement)) return;
    const formatted = this.#formatMoney(Math.max(0, totalCents), currency);
    cartTotal.textContent = formatted;
    cartTotal.setAttribute('value', formatted);
  }

  /**
   * @param {number} cents
   * @param {string} currency
   * @returns {string}
   */
  #formatMoney(cents, currency) {
    const { moneyFormat } = this.refs;
    const template = moneyFormat.content.textContent?.trim() || '{{amount}}';
    const amount = Math.max(0, cents) / 100;

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (placeholder === 'currency') return currency;
      const precision = placeholder === 'amount_no_decimals' ? 0 : 2;
      return amount.toLocaleString('en-GB', { minimumFractionDigits: precision, maximumFractionDigits: precision });
    });
  }

  /** @param {string} s */
  #esc(s) {
    const d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    let operationSucceeded = false;
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    discountCode.removeAttribute('aria-invalid');

    const cartItemsComponent = this.closest('cart-items-component');
    const abortController = this.#createAbortController();

    try {
      if (cartItemsComponent && 'disableCartItems' in cartItemsComponent) {
        cartItemsComponent.disableCartItems();
      }

      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
          sections_url: window.location.pathname,
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountElements = section?.querySelectorAll('[data-discount-code]') || [];
      if (section) {
        const codes = Array.from(discountElements)
          .map((element) => (element instanceof HTMLElement ? element.dataset.discountCode : null))
          .filter(Boolean);

        // Check if the discount produced line-level allocations (e.g., BXGY discounts).
        // Case-insensitive: Shopify discount codes are case-insensitive.
        const lowerCode = discountCodeValue.toLowerCase();
        const hasLineDiscount = data.items?.some((item) =>
          item.line_level_discount_allocations?.some(
            (alloc) => alloc.discount_application?.title?.toLowerCase() === lowerCode
          )
        );

        // Only classify as shipping if no cart-level or line-level discount exists
        if (
          !hasLineDiscount &&
          codes.length === existingDiscounts.length &&
          codes.every((code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
      operationSucceeded = true;
    } catch (error) {
    } finally {
      if (cartItemsComponent && 'enableCartItems' in cartItemsComponent) {
        cartItemsComponent.enableCartItems();
      }
      if (operationSucceeded) this.#closeDisclosure();
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    let operationSucceeded = false;
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const applied = event.target.closest('[data-discount-code]');
    if (!(applied instanceof HTMLElement)) return;

    const discountCode = applied.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const cartItemsComponent = this.closest('cart-items-component');
    const abortController = this.#createAbortController();

    try {
      if (cartItemsComponent && 'disableCartItems' in cartItemsComponent) {
        cartItemsComponent.disableCartItems();
      }

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: existingDiscounts.join(','),
          sections: [this.dataset.sectionId],
          sections_url: window.location.pathname,
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
      operationSucceeded = true;
    } catch (error) {
    } finally {
      if (cartItemsComponent && 'enableCartItems' in cartItemsComponent) {
        cartItemsComponent.enableCartItems();
      }
      if (operationSucceeded) this.#closeDisclosure();
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
    if (type === 'discount_code') {
      const input = this.querySelector('input[name="discount"]');
      if (input instanceof HTMLInputElement) input.setAttribute('aria-invalid', 'true');
    }
  }

  /** @type {any} cast is required — cart-discount.js has no import of DisclosureCustom. */
  #closeDisclosure() {
    const disclosure = this.querySelector('disclosure-custom');
    if (!(disclosure instanceof HTMLElement) || !('toggleDisclosure' in disclosure)) return;
    const trigger = /** @type {any} */ (disclosure).refs?.disclosureTrigger;
    if (trigger instanceof HTMLElement && trigger.getAttribute('aria-expanded') === 'true') {
      /** @type {any} */ (disclosure).toggleDisclosure();
    }
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    const applied = this.querySelector('[data-discount-code]');
    if (applied instanceof HTMLElement && typeof applied.dataset.discountCode === 'string') {
      return [applied.dataset.discountCode];
    }
    return [];
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
